import {
  CheckStatus,
  PolingStatus,
  PreprocessingStatus,
  UploadFormData,
} from '@components/SiteUserUpload/types/uploadTypes';
import { UploadFileTypeEnum } from '@dynamika-shared/types/inventory';

export enum UploadVisitType {
  REGULAR = 'REGULAR',
  UNSCHEDULED = 'UNSCHEDULED',
  EARLY_TERMINATED = 'EARLY TERMINATED',
}
export type CreateVisit = {
  patientId: string;
  visitId?: string;
  visitDefinitionId?: string | null;
  createUnscheduled: boolean;
  earlyTerminated: boolean;
  fileType: UploadFileTypeEnum;
};

export type VisitResponse = {
  id: string;
  patientId: string;
  visitId?: string;
  siteId: string;
  clinicalTrialId: string;
  earlyTerminated: boolean;
  createUnscheduled: boolean;
  visitName: string;
  fileType: UploadFileTypeEnum;
};

export type VerificationStepsItem = {
  id: string;
  status: CheckStatus;
  type: string;
  title: string;
  message: string;
  comment: string;
  index: number;
};

export type FormState = {
  [key: string]: string;
};

export type PolingResponse = {
  preprocessingStatus: PreprocessingStatus;
  status: PolingStatus;
  verificationSteps: VerificationStepsItem[];
};

export enum UploadStatus {
  INITIAL_VIEW = 'INITIAL_VIEW',
  FILE_UPLOAD_IN_PROGRESS = 'FILE_UPLOAD_IN_PROGRESS',
  FILE_UPLOAD_SUCCESS = 'FILE_UPLOAD_SUCCESS',
  FILE_UPLOAD_ERROR = 'FILE_UPLOAD_ERROR',

  FILE_FINALIZE_INIT = 'FILE_FINALIZE_INIT',
  FILE_FINALIZE_ERROR = 'FILE_FINALIZE_ERROR',

  FILE_POLING_SUCCESS = 'FILE_POLING_SUCCESS',
  FILE_POLING_WARNING = 'FILE_POLING_WARNING',
  FILE_POLING_ERROR = 'FILE_POLING_ERROR',
  FILE_DICOM_ERROR = 'FILE_DICOM_ERROR',

  FINALIZE_UPLOAD = 'FINALIZE_UPLOAD',
}

export type DraftVisit = {
  value: string;
  id?: string;
  visitDefinitionId?: string;
  visitName?: string;
};

export type ExistingVisit = {
  daysFromBaseline: number;
  earlyTerminated: boolean;
  expectedDate: string | null;
  id: string;
  latestUpload: string;
  status: 'CREATED' | 'IN_PROGRESS' | 'CLOSED';
  type: UploadVisitType;
  value: string;
  visitDefinitionId: string;
  visitName: string;
};

export type VisitProps = ExistingVisit | DraftVisit;

export type SiteUploadState = {
  data: VisitResponse | null;
  schemas: UploadFormData | null;
  signedUrlResponse: { signedUrl: string } | null;
  edtfData: null | FormState;
  status: null | UploadStatus;
  polingData: null | undefined | PolingResponse;
  loading: boolean;
  error?: string;
  shouldContinuePolling: boolean;
};

export type SiteUploadActions = {
  createVisit: (
    data: CreateVisit,
    selectedClinicalTrial: string,
    selectedSite: string
  ) => Promise<void>;
  getEDTFSchemas: () => Promise<void>;
  clearData: () => void;
  uploadFile: (
    file: File,
    selectedClinicalTrial: string,
    selectedSite: string,
    additionalComment?: string
  ) => Promise<void>;
  getPolingStatus: (
    selectedClinicalTrial: string,
    selectedSite: string
  ) => Promise<void>;

  finalizeUpload: (
    selectedClinicalTrial: string,
    selectedSite: string,
    additionalComment?: string
  ) => Promise<void>;
  updateEDTFSchemas: (
    data: {
      [fieldName: string]: string | number | boolean | null;
    } | null,
    selectedClinicalTrial: string,
    selectedSite: string
  ) => Promise<void>;
  submitUpload: (
    selectedClinicalTrial: string,
    selectedSite: string,
    verificationStepsData: Partial<VerificationStepsItem>[]
  ) => Promise<void>;
  setStatus: (status: UploadStatus) => void;
  setReupload: () => Promise<void>;
  updateEDTFData: (data: FormState) => void;
  cancelUpload: (
    selectedClinicalTrial: string,
    selectedSite: string
  ) => Promise<void>;
  clearError: () => void;
};

export type SiteUploadStore = SiteUploadState & SiteUploadActions;
